import clsx from 'clsx';
import { Button } from '@mui/material';
import { useNotify } from 'react-admin';
import { InputNumber, Select } from 'antd';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import { moneyFormatter } from '@utils';
import { useSettingsContext } from '@providers';
import { CalculateInvoiceResult } from '@network';
import { ProductCreator } from '@components/ProductCreator';
import { productInvoiceHttp } from '@network/product-invoice-http';
import { CreditInvoice, OrderCompositionProduct, Product, ProductOriginType } from '@types';
import cls from './credit-invoice-products.module.css';


const taxesOptions = [
  { label: 'TAX FREE', value: 'tax_free' },
  { label: 'IVA', value: 'iva' },
  { label: 'ISR', value: 'isr' },
  { label: 'IEPS', value: 'ieps' },
];

interface CreditInvoiceProductProps {
  index: number;
  productIds: number[];
  product: OrderCompositionProduct | null;
  productsSuggests: Product[];
  removeProduct: (arg: number) => void;
  onProductSelect: (index: number, productId: number | string, option: any) => void;
  setIsProductCreatorActive: (arg: boolean) => void;
  onQtyChange: (index: number, qty: number) => void;
  onProductPriceChange: (index: number, productPrice: any) => void;
  onProductPackChange: (index: number, productPack: string | null) => void;
  onTaxesChange: (index: number, tax: any) => void;
  addProduct: (index: number) => void;
  disabled?: boolean;
}

const CreditInvoiceProduct = ({
  product,
  productIds,
  index,
  productsSuggests,
  removeProduct,
  onProductSelect,
  onQtyChange,
  onProductPriceChange,
  onProductPackChange,
  onTaxesChange,
  addProduct,
  disabled,
}: CreditInvoiceProductProps) => {
  const { isProductPackActive } = useSettingsContext();
  const productId = product?.productId;
  const productEntity = productsSuggests.find(p => p.id === productId) || null;

  const options = productsSuggests.map(p => ({
    value: p.id.toString(),
    label: p.pack ? `${p.name} - ${p.pack}` : p.name,
    disabled: productIds.includes(p.id),
  }));

  const packOptions = (productEntity?.packOptions || []).map(pack => (
    { value: pack, label: pack }
  ));

  useEffect(() => {
    if (packOptions.length === 1 && product?.productPack !== packOptions[0].value) {
      onProductPackChange(index, packOptions[0].value);
    }
  }, [productId]);

  const filterOption = (input: string, option?: {label: string; value: string}) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const isXml = !!product?.xmlName;
  const xmlName = product?.xmlName;

  const selectedTaxes = product?.taxes || [];
  const isTaxFreeSelected = selectedTaxes.includes('tax_free');
  const isOtherTaxSelected = selectedTaxes.some(
    (tax: string) => ['iva', 'isr', 'ieps'].includes(tax),
  );

  const getTaxesOptions = () => {
    return taxesOptions.map(option => ({
      ...option,
      disabled: option.value === 'tax_free'
        ? isOtherTaxSelected
        : isTaxFreeSelected,
    }));
  };

  const productOptions = disabled ? options : [
    { value: 'Add product', label: 'Add product' },
    ...options,
  ];

  return (
    <div className={clsx(cls.product, isXml && cls.xml)}>
      <div className={cls.productContent}>
        <div className={cls.productHead}>
          <div className={cls.name}>Product</div>
          <div className={cls.quantity}>Quantity</div>
          <div className={cls.price}>Unit Price</div>
          <div className={cls.colTotal}>Unit Total</div>
          <div className={cls.taxes}>Applied taxes</div>
          {isProductPackActive ? (
            <div className={cls.pack}>{packOptions.length > 0 ? 'Pack Size' : ''}</div>
          ) : null}
          {!isXml && (<div className={cls.remove}>Remove</div>)}
        </div>
        <div className={cls.productInfo}>
          <div className={cls.number}>{index + 1}</div>
          <div className={cls.name}>
            {isXml && xmlName && (
              <div className={cls.extraction} title={product.xmlName}>
                [XML] {xmlName.length > 117 ? xmlName.slice(0, 117) + '...' : xmlName}
              </div>
            )}
            <Select
              onChange={(productId, option) => onProductSelect(index, productId, option)}
              value={product?.productId?.toString()}
              className={cls.input}
              showSearch
              placeholder="Add product"
              options={productOptions}
              notFoundContent={(
                <div
                  style={{ margin: '-5px -12px' }}
                  onClick={() => addProduct(index)}
                  aria-selected="false"
                  className="ant-select-item ant-select-item-option ant-select-item-option-active"
                  title="Add product"
                >
                  <div className="ant-select-item-option-content">Add product</div>
                </div>
              )}
              filterOption={filterOption}
              disabled={disabled}
            />
          </div>
          <div className={cls.quantity}>
            <InputNumber
              disabled={!product?.productId || isXml || disabled}
              className={cls.input}
              value={product?.qty}
              onChange={qty => onQtyChange(index, qty || 1)}
              defaultValue={1}
              step={1}
              controls={false}
            />
          </div>
          <div className={cls.price}>
            <InputNumber
              disabled={!product?.productId || isXml || disabled}
              className={cls.input}
              value={product?.productPrice || 0}
              onChange={price => onProductPriceChange(index, price)}
              defaultValue={0}
              min={0}
              step={100}
              formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(val) => val ? +val.replace(/\$\s?|\s?\%|(,*)/g, '') : 0}
              controls={false}
            />
          </div>
          <div className={cls.colTotal}>
            <InputNumber
              className={cls.input}
              value={+((product?.productPrice ?? 0) * (product?.qty ?? 0)).toFixed(2)}
              formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(val) => val ? +val.replace(/\$\s?|\s?\%|(,*)/g, '') : 0}
              controls={false}
              disabled
            />
          </div>
          <div className={cls.taxes}>
            <Select
              className={cls.taxesSelect}
              mode="multiple"
              showSearch
              options={getTaxesOptions()}
              value={selectedTaxes}
              onChange={(value) => onTaxesChange(index, value)}
              disabled={disabled}
            />
          </div>
          {isProductPackActive ? (
            <div className={cls.pack}>
              {packOptions.length > 0 && (
                <Select
                  className={cls.input}
                  showSearch
                  options={packOptions}
                  value={product?.productPack || null}
                  onChange={(value) => onProductPackChange(index, value)}
                  disabled={disabled}
                />
              )}
            </div>
          ) : null}
          {!isXml ? (
            <div
              className={clsx(cls.remove, disabled && cls.disabled)}
              onClick={() => removeProduct(index)}
            >
              <div className={cls.removeIcon}>
                <svg xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="18"
                  viewBox="0 0 14 18"
                  fill="none">
                  <path d="M1 16C1 17.1 1.9 18 3 18H11C12.1 18 13 17.1 13 16V4H1V16ZM10.5 1L9.5 0H4.5L3.5 1H0V3H14V1H10.5Z"
                    fill="#FF4D4F" />
                </svg>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

interface CreditInvoiceProductsProps {
  composition: OrderCompositionProduct[];
  setComposition: Dispatch<SetStateAction<OrderCompositionProduct[]>>;
  externalShop?: string;
  calculate: CalculateInvoiceResult | undefined;
  discountAmount: number;
  setDiscountAmount: (amount: number) => void;
  isrPercent: number | null;
  setIsrPercent: (percent: number | null) => void;
  iepsPercent: number | null;
  setIepsPercent: (percent: number | null) => void;
  invoice?: CreditInvoice;
  disabled?: boolean;
  submit?: (creditId?: number) => Promise<void | CreditInvoice | null>;
  max?: number;
  origin?: ProductOriginType | null;
}

export const CreditInvoiceProducts = ({
  composition, setComposition, externalShop, calculate,
  discountAmount, setDiscountAmount,
  isrPercent, setIsrPercent,
  iepsPercent, setIepsPercent,
  invoice, disabled, submit, max, origin = null,
}: CreditInvoiceProductsProps) => {
  const notify = useNotify();
  const [isProductCreatorActive, setIsProductCreatorActive] = useState<boolean>(false);
  const [productsSuggests, setProductsSuggests] = useState<Product[]>([]);
  const xmlProducts = invoice?.xmlProducts || [];
  const isXml = invoice?.isXml;

  const addPosition = (position: OrderCompositionProduct) => {
    const addedPosition: OrderCompositionProduct = {
      externalProduct: position.externalProduct,
      id: position?.id || null,
      productId: position?.productId || null,
      qty: position.qty ?? 1,
      productPack: position.productPack ?? null,
      productPrice: position.productPrice ?? null,
      taxes: [],
      isExternal: true,
      xmlName: position.xmlName,
      xmlQty: position.xmlQty,
      xmlPrice: position.xmlPrice,
    };
    if (composition.find(c => c.id === null) && !position.xmlName) {
      notify('Fill the last product info before you add the new one.');
      return;
    }
    setComposition([...composition, addedPosition]);
  };

  const removePosition = (index: number) => {
    if (disabled) return;

    const nextComposition = composition.filter((p, i) => i !== index);
    setComposition(nextComposition);
  };

  const addProduct = (index: number) => {
    removePosition(index);
    setIsProductCreatorActive(true);
  };

  const onProductSelect = (
    index: number,
    productId: number | string,
    option: {value: string; label: string},
  ) => {
    if (productId === 'Add product') {
      return addProduct(index);
    }
    if (composition.find(position => position.productId === productId)) {
      return removePosition(index);
    }
    const nextComposition = composition.slice();
    nextComposition[index].id = index;
    nextComposition[index].productId = +productId;
    nextComposition[index].productPack = null;
    nextComposition[index].externalProduct = option?.label || '';
    nextComposition[index].product = productsSuggests.find((product) => product.id === +productId);

    if (!isXml) {
      nextComposition[index].productPrice =
        productsSuggests.find((product) => product.id === +productId)?.price || 0;
    }

    setComposition(nextComposition);
  };

  const onQtyChange = (index: number, qty: any) => {
    const nextComposition = composition.slice();
    nextComposition[index].qty = qty;
    setComposition(nextComposition);
  };

  const onTaxesChange = (index: number, tax: any) => {
    const nextComposition = composition.slice();
    if (index >= 0 && index < nextComposition.length && nextComposition[index]) {
      nextComposition[index].taxes = [...tax];
    }
    setComposition(nextComposition);
  };

  const onProductPriceChange = (index: number, productPrice: any) => {
    const nextComposition = composition.slice();
    nextComposition[index].productPrice = productPrice;
    setComposition(nextComposition);
  };

  const onProductPackChange = (index: number, productPack: string | null) => {
    const nextComposition = composition.slice();
    nextComposition[index].productPack = productPack;
    setComposition(nextComposition);
  };

  const fetchProducts = async () => {
    try {
      const loadedProducts = await productInvoiceHttp.getProducts();
      setProductsSuggests(loadedProducts);
    } catch (e) {
      console.error(e);
    }
  };

  const productIds: number[] = useMemo(() => {
    return (composition || [])
      .map(p => {
        if (!p.productId || isNaN(p.productId)) {
          return 0;
        }

        return Number(p.productId);
      })
      .filter(Boolean);
  }, [composition]);

  useEffect(() => {
    void fetchProducts();
  }, []);

  useEffect(() => {
    if (xmlProducts.length > 0 && !composition[0]?.xmlName) {
      setComposition([]);
      for (const xmlProduct of xmlProducts) {
        const [xmlName, xmlQty, xmlPrice] = xmlProduct.split('|');
        const qty = xmlQty ? +parseFloat(xmlQty).toFixed(3) : 1;
        const productPrice = xmlPrice ? +parseFloat(xmlPrice).toFixed(3) : 0;
        addPosition({
          id: null,
          productId: null,
          qty: qty,
          productPack: null,
          productPrice: productPrice,
          isExternal: true,
          taxes: [],
          xmlName,
          xmlQty,
          xmlPrice,
        });
      }
    }
  }, [xmlProducts]);

  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    if (isProductCreatorActive) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [isProductCreatorActive]);

  const onAddProduct = () => {
    if (isXml) return;
    addPosition({
      id: null,
      productId: null,
      qty: 1,
      productPack: null,
      productPrice: null,
      isExternal: true,
      taxes: [],
    });
  };

  return (
    <div className={clsx(cls._, isXml && cls.xml)}>
      <div className={cls.list}>
        {composition.map((product, index) => (
          <CreditInvoiceProduct
            key={product.id}
            product={product}
            productIds={productIds}
            index={index}
            productsSuggests={productsSuggests}
            removeProduct={removePosition}
            onProductSelect={onProductSelect}
            setIsProductCreatorActive={setIsProductCreatorActive}
            onQtyChange={onQtyChange}
            onProductPriceChange={onProductPriceChange}
            onProductPackChange={onProductPackChange}
            onTaxesChange={onTaxesChange}
            addProduct={addProduct}
            disabled={disabled}
          />
        ))}
        {isProductCreatorActive && (
          <CreditInvoiceProduct
            product={null}
            productIds={productIds}
            index={composition.length}
            productsSuggests={productsSuggests}
            removeProduct={removePosition}
            onProductSelect={onProductSelect}
            setIsProductCreatorActive={setIsProductCreatorActive}
            onQtyChange={onQtyChange}
            onProductPriceChange={onProductPriceChange}
            onProductPackChange={onProductPackChange}
            onTaxesChange={onTaxesChange}
            addProduct={addProduct}
            disabled={disabled}
          />
        )}
      </div>
      <div className={cls.add}>
        {!disabled && !(max && composition.length >= max) && (
          <Button
            variant="contained"
            className={cls.addButton}
            onClick={onAddProduct}
            disabled={isXml}
          >
            Add product
          </Button>
        )}
      </div>

      {/* TOTALS */}
      <div className={cls.totals}>
        <div className={cls.total}>
          <div className={cls.totalLabel}>Sub-total</div>
          <div className={clsx(cls.totalValue, cls.noOpacity)}>
            {moneyFormatter.format(invoice?.xmlSubtotal || calculate?.totalPositions || 0)}
          </div>
        </div>

        {/* IVA */}
        <div className={cls.total}>
          <div className={cls.totalLabel}>
            <div>+ IVA<sub>T</sub> (16%)</div>
            <div style={{ marginLeft: 10 }}>- IVA<sub>R</sub> (10.667%)</div>
          </div>
          <div className={cls.totalValue}>
            {moneyFormatter.format(calculate?.totalIva ?? 0)}
          </div>
        </div>

        {/* ISR */}
        <div className={cls.total}>
          <div className={cls.totalLabel}>
            <div style={{ width: 60 }}>- ISR</div>
            <InputNumber
              disabled={isXml || disabled}
              className={clsx(cls.input, cls.inputPercent)}
              value={isrPercent || null}
              onChange={value => setIsrPercent(value || 0)}
              defaultValue={0}
              step={1}
              min={0}
              max={35}
              maxLength={5}
              placeholder="1.25 - 35%"
              controls={false}
            />
          </div>
          <div className={clsx(cls.totalValue, calculate?.isrAmount && cls.red)}>
            {moneyFormatter.format(calculate?.isrAmount ?? 0)}
          </div>
        </div>

        {/* IEPS */}
        <div className={cls.total}>
          <div className={cls.totalLabel}>
            <div style={{ width: 60 }}>- IEPS</div>
            <InputNumber
              disabled={isXml || disabled}
              className={clsx(cls.input, cls.inputPercent)}
              value={iepsPercent || null}
              onChange={value => setIepsPercent(value || 0)}
              defaultValue={0}
              step={1}
              min={-100}
              max={100}
              maxLength={5}
              placeholder="0 - 100%"
              controls={false}
            />
          </div>
          <div className={clsx(cls.totalValue, calculate?.iepsAmount && cls.red)}>
            {moneyFormatter.format(calculate?.iepsAmount ?? 0)}
          </div>
        </div>

        {/* Total - Discount*/}
        <div className={cls.total}>
          <div className={cls.totalLabel}>
            <div>Total</div>
            <div className={cls.totalDash}>–</div>
            <InputNumber
              disabled={isXml || disabled}
              className={clsx(cls.input, cls.inputMoney)}
              value={discountAmount || undefined}
              onChange={value => setDiscountAmount(value || 0)}
              defaultValue={0}
              step={1}
              placeholder="$ - Discount"
              formatter={(val) => `$ ${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(val) => val ? +val.replace(/\$\s?|\s?\%|(,*)/g, '') : 0}
              controls={false}
              maxLength={12}
            />
          </div>
          <div className={clsx(cls.totalValue, cls.noOpacity)}>
            <b>{moneyFormatter.format(invoice?.xmlTotal || calculate?.finalAmount || 0)}</b>
          </div>
        </div>

        {submit && (
          <div className={cls.actions}>
            <Button onClick={() => submit()}
              className={cls.submit}
              variant="contained"
              type="button">Save</Button>
          </div>
        )}
      </div>

      {isProductCreatorActive && (
        <ProductCreator
          setIsActive={setIsProductCreatorActive}
          addPosition={addPosition}
          fetchProducts={fetchProducts}
          externalShop={externalShop}
          origin={origin}
        />
      )}
    </div>
  );
};
